/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCurrentWorkspace } from '@gripp/shared-logic';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text } from 'react-native';
import { Colors } from '../../themes';
import { BaseColumnCheckbox } from '../baseColumnCheckbox';

export type AlertsManagementTableData = {
  id: string;
  name: string;
  routineName: string;
  assetName: string;
  groupName: string;
  productName: string;
  assetAlerts: number;
  lastAlertDate: string;
  nextAlertDate: string;
  createdAt: string;
  disabled: boolean;
};

const getLabel = (labelStyle: any, label: string | null) => {
  return <Text style={[labelStyle, styles.cellText]}>{label || '-'}</Text>;
};

const getInfoLabel = (
  labelStyle: any,
  info: CellContext<AlertsManagementTableData, unknown>
) => {
  return getLabel(labelStyle, info.getValue() as string);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const BaseColumns: Record<
  string,
  {
    element: (
      info: CellContext<AlertsManagementTableData, unknown>
    ) => React.ReactElement;
    exportCell?: (info: AlertsManagementTableData) => string;
  }
> = {
  name: {
    element: (info) => getInfoLabel(styles.nameLabel, info),
    exportCell: (data) => data.name || '',
  },
  routineName: {
    element: (info) => getInfoLabel(styles.cellLabel, info),
    exportCell: (data) => data.routineName || '',
  },
  assetName: {
    element: (info) => getInfoLabel(styles.cellLabel, info),
    exportCell: (data) => data.assetName || '',
  },
  groupName: {
    element: (info) => getInfoLabel(styles.cellLabel, info),
    exportCell: (data) => data.groupName || '',
  },
  productName: {
    element: (info) => getInfoLabel(styles.cellLabel, info),
    exportCell: (data) => data.productName || '',
  },
  assetAlerts: {
    element: (info) => {
      const count = info.getValue() as number;
      return getLabel(styles.cellLabel, count?.toString() || '0');
    },
    exportCell: (data) => (data.assetAlerts || 0).toString(),
  },
  lastAlertDate: {
    element: (info) =>
      getLabel(styles.dateLabel, formatDate(info.getValue() as string)),
    exportCell: (data) => formatDate(data.lastAlertDate),
  },
  nextAlertDate: {
    element: (info) =>
      getLabel(styles.dateLabel, formatDate(info.getValue() as string)),
    exportCell: (data) => formatDate(data.nextAlertDate),
  },
  createdAt: {
    element: (info) =>
      getLabel(styles.dateLabel, formatDate(info.getValue() as string)),
    exportCell: (data) => formatDate(data.createdAt),
  },
};

export const useColumns = (
  filter: any
): ColumnDef<AlertsManagementTableData, any>[] => {
  const [columns, setColumns] = useState<
    ColumnDef<AlertsManagementTableData, any>[]
  >([]);

  const { t } = useTranslation();
  const columnHelper = createColumnHelper<AlertsManagementTableData>();
  const workspace = useCurrentWorkspace(true);
  const isWeb = Platform.OS === 'web';

  useEffect(() => {
    if (!filter) return;

    const cols: ColumnDef<AlertsManagementTableData, any>[] = [];

    if (isWeb && workspace) {
      cols.push(
        columnHelper.accessor('selected' as any, {
          cell: BaseColumnCheckbox.checkbox,
          header: BaseColumnCheckbox.checkboxHeader,
          meta: { style: styles.checkboxCell },
        })
      );
    }

    if (!filter?.columns) {
      setColumns(cols);
      return;
    }

    // Define the correct column order
    const columnOrder: (keyof typeof BaseColumns)[] = [
      'name',
      'routineName',
      'assetName',
      'groupName',
      'productName',
      'assetAlerts',
      'lastAlertDate',
      'nextAlertDate',
      'createdAt',
    ];

    // Build columns in the correct order, only if they're in the filter
    columnOrder.forEach((column) => {
      if (filter.columns.includes(column)) {
        const baseColumn = BaseColumns[column];
        if (!baseColumn) return;

        cols.push(
          columnHelper.accessor(column as any, {
            cell: baseColumn.element,
            header: () => t(`alertsManagement.list.${column}`),
            meta: {
              sortable: true,
              exportCell: baseColumn.exportCell,
            },
          })
        );
      }
    });

    setColumns(cols);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, workspace, isWeb]);

  return columns;
};

const styles = StyleSheet.create({
  cellText: {
    fontSize: 14,
    flexWrap: 'nowrap',
  },
  nameLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.blackText,
  },
  cellLabel: {
    fontSize: 14,
    color: Colors.blackText,
  },
  dateLabel: {
    fontSize: 14,
    color: Colors.blackText,
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
});
